import React, { FC, ReactNode, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import SendIcon from '@mui/icons-material/Send';
import { useTranslation } from 'react-i18next';
import { AxiosError } from 'axios';
import {
  CommandParameterDescriptor,
  CommandParameterOption,
  CommandParameters,
  CommandType,
  ErrorResponse,
} from '@thingslog/repositories';
import { commandsQueryClient } from '../../../../clients/ReactQueryClients/ReactQueryClients';
import {
  parseParameterName,
  translateCommandType,
  translateParameterName,
} from '../utils/commandLabels';

/**
 * `sx`, not Tailwind: a Dialog portals to `document.body`, and the preset emits
 * every utility as `#root .foo`, which cannot match inside a portal.
 */

const TYPEAHEAD_THRESHOLD = 12;

const WIDE_FORM_PARAMETER_COUNT = 6;

/**
 * `RESET` has no branch in `getCommandParameters`, so asking for its parameters
 * is a permanent 404. It takes none, so treat that 404 as "no parameters".
 */
const isMissingDescriptorError = (error: AxiosError<ErrorResponse> | null): boolean =>
  error?.response?.data?.errorCode === 'COMMAND_TYPE_NOT_FOUND';

const backendMessage = (error: AxiosError<ErrorResponse> | null): string | null =>
  error?.response?.data?.message ?? null;

const SendCommandDialog: FC<SendCommandDialogProps> = ({
  open,
  deviceNumber,
  onClose,
  onSent,
}: SendCommandDialogProps) => {
  const { t } = useTranslation();
  const isNarrow = useMediaQuery(useTheme().breakpoints.down('sm'));
  const [commandType, setCommandType] = useState<CommandType | null>(null);
  const [values, setValues] = useState<CommandParameters>({});
  const [sendError, setSendError] = useState<string | null>(null);

  const { useCommandTypes, useCommandTypeParameters, useCreateCommand } = useMemo(
    () => commandsQueryClient,
    []
  );

  const commandTypesQuery = useCommandTypes({ enabled: open, staleTime: Infinity });

  const parametersQuery = useCommandTypeParameters(open ? commandType : null, deviceNumber, {
    retry: false,
    staleTime: Infinity,
  });

  const createCommand = useCreateCommand({
    onSuccess: (): void => {
      onSent();
      handleClose();
    },
    onError: (error: AxiosError<ErrorResponse>): void => {
      setSendError(backendMessage(error) ?? t('commands_error_send_failed'));
    },
  });

  useEffect((): void => {
    if (!open || commandType !== null) return;
    const first = commandTypesQuery.data?.[0];
    if (first) setCommandType(first);
  }, [open, commandType, commandTypesQuery.data]);

  const descriptors: CommandParameterDescriptor[] = useMemo(
    () => parametersQuery.data ?? [],
    [parametersQuery.data]
  );

  useEffect((): void => {
    setValues((previous: CommandParameters): CommandParameters => {
      const next: CommandParameters = {};
      descriptors.forEach((descriptor: CommandParameterDescriptor): void => {
        const existing = previous[descriptor.parameterName];
        if (existing !== undefined) {
          next[descriptor.parameterName] = existing;
          return;
        }
        if (descriptor['@type'] === 'enum' && descriptor.values.length === 1) {
          next[descriptor.parameterName] = descriptor.values[0].value;
        }
      });
      return next;
    });
  }, [descriptors]);

  const handleClose = (): void => {
    setCommandType(null);
    setValues({});
    setSendError(null);
    onClose();
  };

  const handleTypeChange = (nextType: CommandType): void => {
    setCommandType(nextType);
    setValues({});
    setSendError(null);
  };

  const descriptorError = parametersQuery.error as AxiosError<ErrorResponse> | null;
  const takesNoParameters =
    descriptors.length === 0 &&
    (parametersQuery.isSuccess || isMissingDescriptorError(descriptorError));
  const hasBlockingDescriptorError =
    parametersQuery.isError && !isMissingDescriptorError(descriptorError);

  const emptyEnums = descriptors.filter(
    (descriptor: CommandParameterDescriptor): boolean =>
      descriptor['@type'] === 'enum' && descriptor.values.length === 0
  );

  const isOutOfRange = (descriptor: CommandParameterDescriptor, value: string): boolean => {
    if (descriptor['@type'] !== 'range' || value === '') return false;
    const numeric = Number(value);
    return (
      Number.isNaN(numeric) || numeric < descriptor.startValue || numeric > descriptor.endValue
    );
  };

  const hasInvalidValue = descriptors.some((descriptor: CommandParameterDescriptor): boolean =>
    isOutOfRange(descriptor, values[descriptor.parameterName] ?? '')
  );

  const missingValues = descriptors.filter(
    (descriptor: CommandParameterDescriptor): boolean =>
      (values[descriptor.parameterName] ?? '') === ''
  );

  const canSubmit =
    commandType !== null &&
    !hasBlockingDescriptorError &&
    !parametersQuery.isFetching &&
    emptyEnums.length === 0 &&
    missingValues.length === 0 &&
    !hasInvalidValue &&
    !createCommand.isLoading;

  const handleSubmit = (event: React.FormEvent): void => {
    event.preventDefault();
    if (!canSubmit || commandType === null) return;
    setSendError(null);
    createCommand.mutate({
      deviceNumber,
      // Never null: the backend dereferences the map and 500s.
      deviceCommandRequest: { commandType, commandParameters: values },
    });
  };

  const renderParameterField = (descriptor: CommandParameterDescriptor): ReactNode => {
    const label = translateParameterName(t, descriptor.parameterName);
    const value = values[descriptor.parameterName] ?? '';
    const setValue = (next: string): void => {
      setValues(
        (previous: CommandParameters): CommandParameters => ({
          ...previous,
          [descriptor.parameterName]: next,
        })
      );
    };

    if (descriptor['@type'] === 'range') {
      const outOfRange = isOutOfRange(descriptor, value);
      return (
        <TextField
          key={descriptor.parameterName}
          fullWidth
          required
          size="small"
          type="number"
          label={label}
          value={value}
          error={outOfRange}
          helperText={t('commands_range_hint', {
            min: descriptor.startValue,
            max: descriptor.endValue,
          })}
          inputProps={{ min: descriptor.startValue, max: descriptor.endValue }}
          onChange={(event: React.ChangeEvent<HTMLInputElement>): void =>
            setValue(event.target.value)
          }
        />
      );
    }

    if (descriptor.values.length === 0) {
      return (
        <TextField
          key={descriptor.parameterName}
          fullWidth
          disabled
          error
          size="small"
          label={label}
          value=""
          helperText={t('commands_no_parameter_values')}
        />
      );
    }

    if (descriptor.values.length > TYPEAHEAD_THRESHOLD) {
      const selected =
        descriptor.values.find(
          (option: CommandParameterOption): boolean => option.value === value
        ) ?? null;

      return (
        <Autocomplete<CommandParameterOption>
          key={descriptor.parameterName}
          size="small"
          options={descriptor.values}
          value={selected}
          getOptionLabel={(option: CommandParameterOption): string => option.displayName}
          isOptionEqualToValue={(a: CommandParameterOption, b: CommandParameterOption): boolean =>
            a.value === b.value
          }
          onChange={(_event: unknown, option: CommandParameterOption | null): void =>
            setValue(option?.value ?? '')
          }
          renderInput={(params: object): ReactNode => (
            <TextField {...params} label={label} required />
          )}
        />
      );
    }

    return (
      <TextField
        key={descriptor.parameterName}
        select
        fullWidth
        required
        size="small"
        label={label}
        value={value}
        onChange={(event: React.ChangeEvent<HTMLInputElement>): void =>
          setValue(event.target.value)
        }
      >
        {descriptor.values.map(
          (option: CommandParameterOption): ReactNode => (
            <MenuItem key={option.value} value={option.value}>
              {option.displayName}
            </MenuItem>
          )
        )}
      </TextField>
    );
  };

  const renderParameters = (): ReactNode => {
    if (parametersQuery.isFetching) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
          <CircularProgress size={28} />
        </Box>
      );
    }

    if (hasBlockingDescriptorError) {
      return (
        <Alert severity="error" role="alert">
          {backendMessage(descriptorError) ?? t('commands_error_parameters_failed')}
        </Alert>
      );
    }

    if (takesNoParameters) {
      return <Alert severity="info">{t('commands_no_parameters')}</Alert>;
    }

    const groups = new Map<number | null, CommandParameterDescriptor[]>();
    descriptors.forEach((descriptor: CommandParameterDescriptor): void => {
      const { groupIndex } = parseParameterName(descriptor.parameterName);
      const bucket = groups.get(groupIndex);
      if (bucket) bucket.push(descriptor);
      else groups.set(groupIndex, [descriptor]);
    });

    return (
      <Stack spacing={3}>
        {[...groups.entries()].map(
          ([groupIndex, groupDescriptors]: [
            number | null,
            CommandParameterDescriptor[]
          ]): ReactNode => (
            <Box key={groupIndex ?? 'ungrouped'}>
              {groupIndex !== null && (
                <Typography
                  variant="overline"
                  color="text.secondary"
                  sx={{ display: 'block', mb: 1 }}
                >
                  {t('commands_schedule_group', { index: groupIndex })}
                </Typography>
              )}
              <Box
                sx={{
                  display: 'grid',
                  gap: 2,
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
                }}
              >
                {groupDescriptors.map(renderParameterField)}
              </Box>
            </Box>
          )
        )}
      </Stack>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={(_event: object, reason: string): void => {
        if (createCommand.isLoading || reason === 'backdropClick') return;
        handleClose();
      }}
      disableEscapeKeyDown={createCommand.isLoading}
      fullScreen={isNarrow}
      fullWidth
      maxWidth={descriptors.length > WIDE_FORM_PARAMETER_COUNT ? 'md' : 'sm'}
      sx={{
        '& .MuiInputBase-root': { minHeight: { xs: 44, sm: 'auto' } },
        '& .MuiDialogActions-root .MuiButton-root': { minHeight: { xs: 44, sm: 'auto' } },
        '& form': {
          display: { xs: 'flex', sm: 'block' },
          flexDirection: 'column',
          height: { xs: '100%', sm: 'auto' },
        },
        '& .MuiDialogContent-root': { flexGrow: { xs: 1, sm: 0 } },
      }}
    >
      <form onSubmit={handleSubmit}>
        <DialogTitle sx={{ pb: 1 }}>
          {t('commands_send_command')}
          <Typography variant="body2" color="text.secondary">
            {deviceNumber}
          </Typography>
        </DialogTitle>

        <DialogContent dividers sx={{ px: 3, py: 3 }}>
          <Stack spacing={3}>
            <TextField
              select
              fullWidth
              autoFocus
              size="small"
              label={t('commands_command_type')}
              value={commandType ?? ''}
              disabled={commandTypesQuery.isLoading}
              onChange={(event: React.ChangeEvent<HTMLInputElement>): void =>
                handleTypeChange(event.target.value as CommandType)
              }
            >
              {(commandTypesQuery.data ?? []).map(
                (type: CommandType): ReactNode => (
                  <MenuItem key={type} value={type}>
                    {translateCommandType(t, type)}
                  </MenuItem>
                )
              )}
            </TextField>

            {renderParameters()}

            {sendError && (
              <Alert severity="error" role="alert">
                {sendError}
              </Alert>
            )}
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button onClick={handleClose} color="inherit" disabled={createCommand.isLoading}>
            {t('commands_btn_close')}
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={!canSubmit}
            startIcon={
              createCommand.isLoading ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <SendIcon />
              )
            }
          >
            {t('commands_btn_submit')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

interface SendCommandDialogProps {
  open: boolean;
  deviceNumber: string;
  onClose: () => void;
  onSent: () => void;
}

export default SendCommandDialog;
